import { useEffect, useMemo, useRef, useState } from "react";
import { allLessons, course, evaluateSeed, ja, layoutById, localDateKey, makeDailyChallenge, makeFreeChallenge, profileFor } from "./data";
import { isDictionaryEntry } from "./dictionary";
import PhysicsCanvas from "./game/PhysicsCanvas";
import DetonateHint from "./components/detonate-hint/DetonateHint";
import { CUSTOM_PREFIX, customLayout, customLesson, evaluateCustom, loadCustomStages } from "./custom/customStages";
import StageIntro, { readIntroVariant } from "./components/stage-intro/StageIntro";
import { Icon, type IconName } from "./icons";
import { firstStageOfNextLesson, nextStageInLesson } from "./progression";
import { dailyStreak, readBestRecords, readDailyRecords, recordBest, writeBestRecords, writeDailyRecords, type BestRecords, type DailyRecords } from "./records";
import { deleteLessonDraft, getCachedEvaluation, getLessonDraft, saveCachedEvaluation, saveLessonDraft } from "./storage";
import { setThemeChoice, themes, type ThemeId } from "./themes/theme";
import type { Evaluation, Layout, Lesson, ReasonLanguage } from "./types";

type Screen = "course" | "game";
type Mode = "course" | "free" | "daily" | "custom";

const findLayout = (id: string) => (layoutById.get(id) ?? customLayout(id))!;
type Phase = "idle" | "checking" | "validating" | "armed" | "resolving" | "feedback";

const powerMeta: Array<{ name: string; icon: IconName; color: string }> = [
  { name: "重さ", icon: "heavy", color: "var(--heavy)" },
  { name: "速さ", icon: "speed", color: "var(--speed)" },
  { name: "爆発", icon: "blast", color: "var(--blast)" },
];

const unitCopy: Record<number, { title: string; summary: string }> = {
  1: { title: "基本を知ろう", summary: "ガラス・積層・レンガで弾の動きがどう変わるか学ぼう。" },
  2: { title: "素材の力", summary: "連鎖・吸収・勢いを使い分けよう。" },
  3: { title: "ルートと反応", summary: "盤面を読み、突破するルートを選ぼう。" },
  4: { title: "総仕上げ", summary: "すべての素材とことばの力を組み合わせよう。" },
};

function readCompleted(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem("wordphysics.completed") ?? "[]") as string[]); }
  catch { return new Set(); }
}

function validateWord(value: string): boolean {
  return value.length > 0 && value.length <= 40 && /^[a-z][a-z '\-]*$/i.test(value);
}

function StatusPill({ shots, goals, blocks }: { shots: number; goals: number; blocks: number }) {
  return (
    <div className="status-pill" aria-label={`残りの弾 ${shots}、残りのコア ${goals}、残りのブロック ${blocks}`}>
      <div><span className="status-value">{shots}</span><small>SHOTS</small></div>
      <i />
      <div><span className="status-value">{goals}</span><small>CORES</small></div>
      <i />
      <div><span className="status-value">{blocks}</span><small>BLOCKS</small></div>
    </div>
  );
}

function formatDay(dateKey: string): string {
  const [, month, day] = dateKey.split("-").map(Number);
  const weekday = "日月火水木金土"[new Date(`${dateKey}T12:00:00`).getDay()];
  return `${month}月${day}日（${weekday}）`;
}

function ShotPips({ total, left }: { total: number; left?: number }) {
  return <span className="shot-pips" aria-label={left === undefined ? undefined : `ベスト残弾 ${left} / ${total}`}>{Array.from({ length: total }, (_, index) => <i key={index} className={left !== undefined && index < left ? "on" : ""} />)}</span>;
}

function PlayCard({ icon, kicker, sub, title, body, meta, action, label, onStart }: {
  icon: IconName;
  kicker: string;
  sub: string;
  title: string;
  body: string;
  meta: string[];
  action: string;
  label: string;
  onStart: () => void;
}) {
  return (
    <button type="button" className="free-card" onClick={onStart} aria-label={label}>
      <div className="free-card-header">
        <span className="free-card-icon"><Icon name={icon} size={19} /></span>
        <span><small>{kicker}</small><b>{sub}</b></span>
      </div>
      <div className="free-card-copy">
        <h2>{title}</h2>
        <p>{body}</p>
      </div>
      <div className="free-card-meta" aria-hidden="true">{meta.map((item, index) => <span key={item}>{index > 0 && <i />}{item}</span>)}</div>
      <span className="free-card-action"><strong>{action}</strong><Icon name="arrow-right" size={19} /></span>
    </button>
  );
}

function CourseScreen({ completed, best, daily, onOpenLesson, onOpenCustom, onFree, onDaily, onSettings }: {
  onOpenCustom: (lesson: Lesson) => void;
  completed: Set<string>;
  best: BestRecords;
  daily: DailyRecords;
  onOpenLesson: (lesson: Lesson) => void;
  onFree: () => void;
  onDaily: () => void;
  onSettings: () => void;
}) {
  const nextLesson = allLessons.find((lesson, index) => index === 0 || completed.has(allLessons[index - 1].id)) && allLessons.find((lesson) => !completed.has(lesson.id));
  const today = localDateKey();
  const todayLeft = daily[today];
  const streak = dailyStreak(daily, today);
  const customLessons = useMemo(() => loadCustomStages().map(customLesson), []);

  return (
    <main className="course-shell">
      <aside className="course-sidebar">
        <div className="brand-block">
          <div className="brand-with-icon"><span className="brand-symbol"><Icon name="book" size={24} /></span><span>Word Physics</span></div>
          <span className="brand-edition">A NOTEBOOK OF WORDS & FORCES</span>
          <p>ことばが力になる。</p>
        </div>
        <div className="progress-box">
          <div><strong className="eyebrow">YOUR PROGRESS</strong><b>{completed.size} / {allLessons.length}</b></div>
          <div className="progress-squares" role="progressbar" aria-label="ステージ進捗" aria-valuemin={0} aria-valuemax={allLessons.length} aria-valuenow={completed.size}>{allLessons.map((lesson) => <span key={lesson.id} className={completed.has(lesson.id) ? "complete" : lesson.id === nextLesson?.id ? "next" : ""} />)}</div>
          <small>{nextLesson ? `次：ステージ${nextLesson.code}・${ja(nextLesson.clue_set.family.charAt(0).toUpperCase() + nextLesson.clue_set.family.slice(1))}` : "すべてのレッスンをクリアしました。"}</small>
        </div>
        <PlayCard
          icon="calendar" kicker="DAILY" sub={formatDay(today)} title="今日の一問"
          body="全員が同じ盤面とお題に挑戦。毎日0時に入れ替わります。"
          meta={[todayLeft !== undefined ? `クリア済み・残り弾 ${todayLeft}` : "3発勝負", streak > 0 ? `${streak}日連続` : "毎日更新"]}
          action={todayLeft !== undefined ? "もう一度挑戦" : "挑戦する"} label="今日の一問を開始" onStart={onDaily}
        />
        <PlayCard
          icon="shuffle" kicker="FREE PLAY" sub="いつでも挑戦" title="フリーチャレンジ"
          body="毎回変わる盤面を、3発でクリアしよう。" meta={["ランダム盤面", "3発勝負"]}
          action="チャレンジ開始" label="フリーチャレンジを開始" onStart={onFree}
        />
        <button className="settings-wide pressable" onClick={onSettings}><Icon name="gear" />設定</button>
      </aside>

      <section className="course-main">
        <header className="course-heading"><span className="eyebrow">THE SYLLABUS / 04 LESSONS · 12 STAGES</span><h1 lang="en">Words are power.</h1><p>単語の意味が、重さ・速さ・爆発に変わる。<br />3つの手がかりから、自分だけの一語を見つけよう。</p></header>
        <div className="lesson-list">
          {course.units.map((unit) => {
            const copy = unitCopy[unit.number] ?? { title: ja(unit.title), summary: ja(unit.summary) };
            return (
              <section className="lesson-section" key={unit.id} aria-labelledby={`unit-${unit.id}`}>
                <header className="lesson-heading"><span className="lesson-number">{String(unit.number).padStart(2, "0")}</span><div><h2 id={`unit-${unit.id}`}>{copy.title}</h2><p>{copy.summary}</p></div><span className="lesson-count">{unit.lessons.filter((lesson) => completed.has(lesson.id)).length} / 3</span></header>
                <div className="stage-list">
                  {unit.lessons.map((lesson) => {
                    const globalIndex = allLessons.findIndex((candidate) => candidate.id === lesson.id);
                    const unlocked = globalIndex === 0 || completed.has(allLessons[globalIndex - 1].id);
                    const done = completed.has(lesson.id);
                    return <button key={lesson.id} className={`stage-row ${done ? "complete" : unlocked ? "next" : "locked"}`} disabled={!unlocked} onClick={() => onOpenLesson(lesson)}>
                      <span className="stage-code">STAGE {lesson.code}</span><span className="stage-family">{ja(lesson.clue_set.family.charAt(0).toUpperCase() + lesson.clue_set.family.slice(1))}{unlocked && <small lang="en">{lesson.clue_set.clues.map((clue) => clue.text).join(" · ")}</small>}</span><span className="stage-state">{done ? <><ShotPips total={lesson.shots} left={best[lesson.id]} /><Icon name="check" size={15} />クリア</> : unlocked ? <>次へ<Icon name="arrow-right" size={16} /></> : <><Icon name="lock" size={13} />ロック</>}</span>
                    </button>;
                  })}
                </div>
              </section>
            );
          })}
          {(
            <section className="lesson-section" aria-labelledby="unit-custom">
              <header className="lesson-heading"><span className="lesson-number">{String(course.units.length + 1).padStart(2, "0")}</span><div><h2 id="unit-custom">カスタムステージ</h2><p>自分で作ったステージで遊ぼう。</p></div><span className="lesson-count">{customLessons.filter((lesson) => best[lesson.id] !== undefined).length} / {customLessons.length}</span></header>
              <div className="stage-list">
                {customLessons.map((lesson) => (
                  <button key={lesson.id} className={`stage-row ${best[lesson.id] !== undefined ? "complete" : "next"}`} onClick={() => onOpenCustom(lesson)}>
                    <span className="stage-code">{lesson.code}</span><span className="stage-family">{ja(lesson.clue_set.family.charAt(0).toUpperCase() + lesson.clue_set.family.slice(1))}<small lang="en">{lesson.clue_set.clues.map((clue) => clue.text).join(" · ")}</small></span><span className="stage-state">{best[lesson.id] !== undefined ? <><ShotPips total={lesson.shots} left={best[lesson.id]} /><Icon name="check" size={15} />クリア</> : <>遊ぶ<Icon name="arrow-right" size={16} /></>}</span>
                  </button>
                ))}
                <a className="stage-row create-stage" href="/create"><span className="stage-code">＋ NEW</span><span className="stage-family">ステージを作る</span><span className="stage-state">作成<Icon name="arrow-right" size={16} /></span></a>
              </div>
            </section>
          )}
        </div>
        <footer className="course-footer"><span>WORD PHYSICS</span><span>意味を考える。力を試す。</span></footer>
      </section>
    </main>
  );
}

function Keyboard({ onKey }: { onKey: (key: string) => void }) {
  return (
    <div className="keyboard" aria-label="英字キーボード">
      {["QWERTYUIOP", "ASDFGHJKL", "ZXCVBNM"].map((row, rowIndex) => (
        <div className={`key-row row-${rowIndex}`} key={row}>
          {Array.from(row).map((key) => <button key={key} onClick={() => onKey(key.toLowerCase())}>{key}</button>)}
          {rowIndex === 2 && <button className="delete-key" aria-label="1文字削除" onClick={() => onKey("BACKSPACE")}><Icon name="delete" size={19} /></button>}
        </div>
      ))}
    </div>
  );
}

function SettingsModal({ online, setOnline, reasonLanguage, setReasonLanguage, theme, setTheme, apiConnected, onClose }: {
  theme: ThemeId;
  setTheme: (value: ThemeId) => void;
  online: boolean;
  setOnline: (value: boolean) => void;
  reasonLanguage: ReasonLanguage;
  setReasonLanguage: (value: ReasonLanguage) => void;
  apiConnected: boolean;
  onClose: () => void;
}) {
  const materials = [
    ["glass", "ガラス", "1ダメージで壊れる"], ["laminated", "積層ガラス", "2ダメージで壊れる"],
    ["brick", "レンガ", "3ダメージで壊れる"], ["core", "コア", "レッスンのターゲット"],
    ["resonance", "エコー", "隣接ブロックへ連鎖"], ["dampener", "アブソーバー", "弾の速度を大きく奪う"],
  ];
  return (
    <div className="modal-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="settings-modal" role="dialog" aria-modal="true" aria-labelledby="settings-title">
        <header><h2 id="settings-title">設定</h2><button onClick={onClose}>完了</button></header>
        <div className="settings-content">
          <h3>このアプリについて</h3>
          <div className="settings-group"><div><span>レッスン</span><b>4</b></div><div><span>ステージ</span><b>12</b></div><div><span>品詞</span><b>Noun · Verb · Adjective</b></div></div>
          <h3>テーマ</h3>
          <div className="settings-group language-setting">
            <div className="segmented" role="radiogroup" aria-label="テーマ">
              {themes.map((item) => (
                <button key={item.id} type="button" role="radio" aria-checked={theme === item.id} className={theme === item.id ? "selected" : ""} onClick={() => setTheme(item.id)}>{item.label}</button>
              ))}
            </div>
            <p>{themes.find((item) => item.id === theme)?.summary}</p>
          </div>
          <h3>解説</h3>
          <div className="settings-group language-setting">
            <div className="segmented" role="radiogroup" aria-label="解説の言語">
              {([["en", "English"], ["ja", "日本語"]] as const).map(([value, label]) => (
                <button key={value} type="button" role="radio" aria-checked={reasonLanguage === value} className={reasonLanguage === value ? "selected" : ""} onClick={() => setReasonLanguage(value)}>{label}</button>
              ))}
            </div>
            <p>{reasonLanguage === "en" ? "採点理由を英語で表示します。収録済みの単語と同じ言語です。" : "採点理由を日本語で表示します。収録済みの単語は、オンライン採点を許可している場合に日本語の解説を取得します。"}</p>
          </div>
          <h3>プライバシー</h3>
          <div className="settings-group privacy-setting">
            <div className="api-status"><span>オンライン採点API</span><b className={apiConnected ? "connected" : "disconnected"}>{apiConnected ? "● 接続済み" : "○ 未接続"}</b></div>
            <label><span>オンライン採点を許可</span><input type="checkbox" checked={online} onChange={(event) => setOnline(event.target.checked)} /><i /></label>
            <p>収録済み単語は端末内で採点します。その他の単語は、オンライン採点を許可した場合のみ本サービスとOpenAIへ送信されます。入力中の単語と採点結果はこのブラウザに保存されます。</p>
          </div>
          <h3>ブロックの素材</h3>
          <div className="settings-group materials">{materials.map(([kind, name, detail]) => <div key={kind}><span className={`material-swatch ${kind}`}>{kind === "core" ? "★" : "◆"}</span><span><b>{name}</b><small>{detail}</small></span></div>)}</div>
        </div>
      </section>
    </div>
  );
}

function OnlineScoringGuide({ apiConnected, onAllow, onDecline }: {
  apiConnected: boolean;
  onAllow: () => void;
  onDecline: () => void;
}) {
  return (
    <div className="modal-backdrop online-guide-backdrop">
      <section className="online-guide" role="dialog" aria-modal="true" aria-labelledby="online-guide-title" aria-describedby="online-guide-description">
        <span className="online-guide-icon"><Icon name="globe" size={31} /></span>
        <small>最初の設定</small>
        <h2 id="online-guide-title">もっと多くの英単語で遊ぼう</h2>
        <p id="online-guide-description">収録されていない英単語も、オンライン採点を使うと意味に合わせて重さ・速さ・爆発をチャージできます。</p>
        <div className="online-guide-points">
          <div><Icon name="check" size={18} /><span><b>入力途中は送信しません</b><small>辞書で英単語と確認できた場合だけ採点します。</small></span></div>
          <div><Icon name="check" size={18} /><span><b>結果はブラウザに保存</b><small>同じ問題と単語なら保存結果を再利用します。</small></span></div>
        </div>
        <div className={`online-guide-status ${apiConnected ? "connected" : "checking"}`}><i />{apiConnected ? "オンライン採点を利用できます" : "オンライン採点の接続を確認中です"}</div>
        <div className="online-guide-actions">
          <button type="button" onClick={onDecline}>今は使わない</button>
          <button type="button" className="primary" onClick={onAllow} autoFocus>オンにして始める</button>
        </div>
        <p className="online-guide-note">設定はあとから変更できます。詳しくは<a href="/privacy" target="_blank" rel="noreferrer">プライバシーポリシー</a>をご覧ください。</p>
      </section>
    </div>
  );
}

function ResultCard({ cleared, mode, lesson, unitNumber, nextStage, nextLessonStage, result, best, onBack, onRetry, onNewChallenge, onLoad, onRestartCourse }: {
  cleared: boolean;
  mode: Mode;
  lesson: Lesson;
  unitNumber?: number;
  nextStage?: Lesson;
  nextLessonStage?: Lesson;
  result: { word: string; shotsLeft: number; improved: boolean } | null;
  best?: number;
  onBack: () => void;
  onRetry: () => void;
  onNewChallenge: () => void;
  onLoad: (lesson: Lesson) => void;
  onRestartCourse: () => void;
}) {
  const title = !cleared ? "あと一歩" : mode === "custom" ? "カスタムクリア" : mode === "daily" ? "今日の一問、クリア" : mode === "free" ? "チャレンジクリア" : nextStage ? "ステージクリア" : "レッスンクリア";
  const detail = !cleared ? "弾を使い切りました。別の単語なら、違う飛び方をするかもしれません。"
    : mode === "custom" ? "ほかのカスタムステージにも挑戦しよう。"
    : mode === "daily" ? "明日はまた新しい盤面とお題が出ます。"
    : mode === "free" ? "次の盤面にも挑戦してみよう。"
    : nextStage ? `次はステージ ${nextStage.code} です。`
    : nextLessonStage && unitNumber ? `レッスン ${unitNumber} を修了。次はレッスン ${unitNumber + 1} です。`
    : "全4レッスン・12ステージを修了しました。";
  const primary = !cleared ? <button className="primary" onClick={mode === "free" ? onNewChallenge : onRetry}>{mode === "free" ? "新しい盤面" : "もう一度"}</button>
    : mode === "free" ? <button className="primary" onClick={onNewChallenge}>新しい盤面</button>
    : mode === "daily" || mode === "custom" ? <button className="primary" onClick={onRetry}>もう一度</button>
    : nextStage ? <button className="primary" onClick={() => onLoad(nextStage)}>次のステージへ</button>
    : nextLessonStage ? <button className="primary" onClick={() => onLoad(nextLessonStage)}>次のレッスンへ</button>
    : <button className="primary restart-course" onClick={onRestartCourse}>コースをやり直す</button>;
  return (
    <div className="result-overlay">
      <div className={`result-card ${cleared ? "cleared" : "failed"}`} role="dialog" aria-labelledby="result-title">
        <span className="eyebrow">{mode === "course" ? `STAGE ${lesson.code}` : mode === "custom" ? lesson.code : mode === "daily" ? "DAILY" : "FREE PLAY"}</span>
        <h2 id="result-title">{title}</h2>
        {cleared && result && <dl className="result-stats">
          <div><dt>WORD</dt><dd className="result-word" lang="en">{result.word || "—"}</dd></div>
          <div><dt>SHOTS LEFT</dt><dd>{result.shotsLeft}<small> / {lesson.shots}</small></dd></div>
          {mode !== "free" && best !== undefined && <div><dt>BEST</dt><dd>{best}{result.improved && <em>更新</em>}</dd></div>}
        </dl>}
        <p>{detail}</p>
        <div className="result-actions">
          <button onClick={onBack}>{mode === "course" ? "コースへ戻る" : "ホームへ"}</button>
          {primary}
        </div>
      </div>
    </div>
  );
}

function GameScreen({ initialLesson, mode, completed, setCompleted, best, onCleared, onBack, onSettings, online, onEnableOnline, reasonLanguage, setReasonLanguage }: {
  initialLesson: Lesson;
  mode: Mode;
  best: BestRecords;
  /** Stores the result and reports whether it beat a previous best. */
  onCleared: (lesson: Lesson, shotsLeft: number) => boolean;
  setReasonLanguage: (value: ReasonLanguage) => void;
  completed: Set<string>;
  setCompleted: (value: Set<string>) => void;
  onBack: () => void;
  onSettings: () => void;
  online: boolean;
  onEnableOnline: () => void;
  reasonLanguage: ReasonLanguage;
}) {
  // Free and daily boards are one-off, so they never keep drafts or course progress.
  const freeMode = mode !== "course";
  const [lesson, setLesson] = useState(initialLesson);
  const [layout, setLayout] = useState<Layout>(() => findLayout(initialLesson.layout_id));
  const [text, setText] = useState("");
  const [evaluation, setEvaluation] = useState<Evaluation | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [introOpen, setIntroOpen] = useState(true);
  const [canDetonateNow, setCanDetonateNow] = useState(false);
  const introVariant = useMemo(readIntroVariant, []);
  const [message, setMessage] = useState("英単語を作って、コアをすべて壊そう。");
  const [shots, setShots] = useState(initialLesson.shots);
  const [blocks, setBlocks] = useState(layout.grid.cells.length);
  const [goals, setGoals] = useState(layout.grid.cells.filter((cell) => cell.goal).length);
  const [fireNonce, setFireNonce] = useState(0);
  const [resetNonce, setResetNonce] = useState(0);
  const [offlineMessage, setOfflineMessage] = useState("");
  const [clearResult, setClearResult] = useState<{ word: string; shotsLeft: number; improved: boolean } | null>(null);
  const completedRef = useRef(completed);
  completedRef.current = completed;
  const restoringDraft = useRef(true);
  const draftGeneration = useRef(0);
  const shotsRef = useRef(shots);
  const gameContentRef = useRef<HTMLElement>(null);
  const playfieldRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLElement>(null);
  shotsRef.current = shots;
  // While a shot resolves or a result is shown, setting changes must not re-score or re-arm the word.
  const busyRef = useRef(false);
  busyRef.current = phase === "resolving" || phase === "feedback";
  const setLivePhase = (next: Phase) => { if (!busyRef.current) setPhase(next); };
  const arm = (nextMessage: string) => { if (!busyRef.current) { setPhase("armed"); setMessage(nextMessage); } };
  const tiersKey = evaluation?.tiers.join(",");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const profile = useMemo(() => evaluation ? profileFor(evaluation.tiers) : null, [tiersKey]);
  const isCleared = phase === "feedback" && goals === 0;
  const isFailed = phase === "feedback" && goals > 0 && shots === 0;
  const currentUnit = course.units.find((unit) => unit.lessons.some((item) => item.id === lesson.id));
  const nextStage = !freeMode && currentUnit ? nextStageInLesson(currentUnit, lesson.id) : undefined;
  const nextLessonStage = !freeMode && currentUnit && !nextStage ? firstStageOfNextLesson(course.units, currentUnit.id) : undefined;
  const apiAvailable = import.meta.env.PROD || Boolean(import.meta.env.VITE_SEMANTIC_API_URL);

  const showPanel = (panel: HTMLElement | null, behavior?: ScrollBehavior) => {
    const content = gameContentRef.current;
    if (!content || !panel) return;
    const padding = Number.parseFloat(window.getComputedStyle(content).paddingLeft) || 0;
    content.scrollTo({
      left: panel.offsetLeft - content.offsetLeft - padding,
      behavior: behavior ?? (window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth"),
    });
  };

  const launchFromDock = () => {
    if (window.matchMedia("(max-width: 650px)").matches) showPanel(playfieldRef.current, "auto");
    setFireNonce((value) => value + 1);
  };

  const restoreDraft = (nextLesson: Lesson) => {
    const generation = ++draftGeneration.current;
    restoringDraft.current = true;
    // Free challenges get a fresh id each time, so there is never a draft to restore.
    if (freeMode) { restoringDraft.current = false; return; }
    // A stage that is already cleared starts empty, even if a draft survived from an older version.
    if (completedRef.current.has(nextLesson.id)) {
      restoringDraft.current = false;
      void deleteLessonDraft(nextLesson.id);
      return;
    }
    void getLessonDraft(nextLesson.id).then((draft) => {
      if (generation !== draftGeneration.current) return;
      if (draft) {
        setText(draft.text);
        setEvaluation(draft.evaluation);
        if (draft.evaluation) {
          setPhase("armed");
          setMessage("保存した単語と採点結果を復元しました。");
        }
      }
    }).finally(() => {
      if (generation === draftGeneration.current) restoringDraft.current = false;
    });
  };

  const loadSession = (nextLesson: Lesson, nextLayout = findLayout(nextLesson.layout_id)) => {
    setLesson(nextLesson); setLayout(nextLayout); setText(""); setEvaluation(null); setPhase("idle"); setOfflineMessage(""); setClearResult(null);
    setShots(nextLesson.shots); setBlocks(nextLayout.grid.cells.length); setGoals(nextLayout.grid.cells.filter((cell) => cell.goal).length);
    setMessage(freeMode ? "3発でコアをすべて壊そう。" : "英単語を作って、コアをすべて壊そう。");
    setResetNonce((value) => value + 1);
    setIntroOpen(true);
    restoreDraft(nextLesson);
  };

  const newChallenge = () => {
    const challenge = makeFreeChallenge();
    loadSession(challenge.lesson, challenge.layout);
  };

  useEffect(() => {
    restoreDraft(initialLesson);
    // The initial lesson is fixed for the lifetime of this game screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (restoringDraft.current || freeMode || isCleared) return;
    void saveLessonDraft(lesson.id, text, evaluation);
  }, [lesson.id, text, evaluation, freeMode, isCleared]);

  useEffect(() => {
    const normalized = text.trim().toLowerCase().replace(/\s+/g, " ");
    if (busyRef.current) return;
    setEvaluation(null); setOfflineMessage("");
    if (!normalized) { setLivePhase("idle"); return; }
    if (!validateWord(normalized)) { setLivePhase("idle"); return; }
    const clueSetId = lesson.clue_set.id;
    const endpoint = (import.meta.env.VITE_SEMANTIC_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";
    const canReachApi = online && Boolean(endpoint || import.meta.env.PROD);
    const controller = new AbortController();
    let cancelled = false;

    const requestRemote = async (): Promise<Evaluation | null> => {
      const response = await fetch(`${endpoint}/evaluate`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        signal: controller.signal,
        body: JSON.stringify({ clue_set_id: clueSetId, candidate: normalized, reason_language: reasonLanguage }),
      });
      if (!response.ok) throw new Error("remote scoring failed");
      const result = await response.json() as { candidate_valid: boolean; tiers: number[]; reasons: string[]; detected_parts_of_speech?: string[] };
      if (!result.candidate_valid) {
        void saveCachedEvaluation(clueSetId, normalized, reasonLanguage, false, null);
        return null;
      }
      const remote: Evaluation = { word: normalized, tiers: result.tiers, reasons: result.reasons, detected_parts_of_speech: result.detected_parts_of_speech, source: "remote", reason_language: reasonLanguage };
      void saveCachedEvaluation(clueSetId, normalized, reasonLanguage, true, remote);
      return remote;
    };

    if (clueSetId.startsWith(CUSTOM_PREFIX)) {
      setLivePhase("checking");
      void evaluateCustom(normalized, lesson).then((result) => {
        if (cancelled) return;
        if (result) { setEvaluation(result); arm("オレンジのボールを引いて、狙いを定めて離そう。"); return; }
        setLivePhase("idle");
        setOfflineMessage("この単語は採点データにありません。別の単語を試そう。");
      });
      return () => { cancelled = true; };
    }

    const local = evaluateSeed(normalized, clueSetId);
    if (local) {
      setEvaluation(local); arm("オレンジのボールを引いて、狙いを定めて離そう。");
      if (reasonLanguage === "en") return () => { cancelled = true; };
      // Bundled reasons are English. Swap in Japanese ones without touching the seed tiers.
      void (async () => {
        try {
          const cached = await getCachedEvaluation(clueSetId, normalized, reasonLanguage);
          const translated = cached?.evaluation ?? (canReachApi ? await requestRemote() : null);
          if (cancelled || !translated || translated.reasons.length !== local.reasons.length) return;
          setEvaluation((current) => current?.word === normalized ? { ...current, reasons: translated.reasons, reason_language: reasonLanguage } : current);
        } catch {
          // Keep the English reasons when the explanation cannot be fetched.
        }
      })();
      return () => { cancelled = true; controller.abort(); };
    }

    setLivePhase("checking");
    const timer = window.setTimeout(async () => {
      if (!await isDictionaryEntry(normalized)) {
        if (!cancelled) {
          setLivePhase("idle");
          setOfflineMessage("辞書に載っている英単語を入力してください。");
        }
        return;
      }

      const cached = await getCachedEvaluation(clueSetId, normalized, reasonLanguage);
      if (cancelled) return;
      if (cached) {
        if (cached.candidateValid && cached.evaluation) {
          setEvaluation(cached.evaluation);
          arm("保存済みの採点結果を読み込みました。");
        } else {
          setLivePhase("idle");
          setOfflineMessage("この入力は英単語として採点できませんでした。");
        }
        return;
      }

      if (canReachApi) {
        setLivePhase("validating");
        try {
          const remote = await requestRemote();
          if (cancelled) return;
          if (!remote) {
            setLivePhase("idle");
            setOfflineMessage("この入力は英単語として採点できませんでした。");
            return;
          }
          setEvaluation(remote);
          arm("オレンジのボールを引いて、狙いを定めて離そう。"); return;
        } catch (error) {
          if (error instanceof DOMException && error.name === "AbortError") return;
        }
      }
      if (!cancelled) {
        setLivePhase("idle");
        setOfflineMessage(online ? "オンライン採点に失敗しました。少し待ってからもう一度試してください。" : "この単語は未収録です。オンライン採点で意味を調べられます。");
      }
    }, 240);
    return () => { cancelled = true; controller.abort(); window.clearTimeout(timer); };
  }, [text, lesson.clue_set.id, online, reasonLanguage]);

  const setWord = (next: string) => {
    if (phase === "resolving" || isCleared || isFailed) return;
    setText(next.slice(0, 40));
  };

  const switchLesson = (next: Lesson) => {
    const index = allLessons.findIndex((item) => item.id === next.id);
    if (index > 0 && !completed.has(allLessons[index - 1].id)) return;
    loadSession(next);
  };

  const retry = () => {
    setClearResult(null);
    setShots(lesson.shots); setPhase(evaluation ? "armed" : "idle"); setMessage("盤面をリセットしました。もう一度狙おう。"); setResetNonce((value) => value + 1);
  };

  const restartCourse = () => {
    setCompleted(new Set());
    loadSession(allLessons[0]);
  };

  return (
    <main className="game-shell">
      {introOpen && <StageIntro variant={introVariant} onDismiss={() => setIntroOpen(false)}
        stageLabel={mode === "custom" ? lesson.code : freeMode ? (mode === "daily" ? "今日の一問" : "フリーチャレンジ") : `STAGE ${lesson.code}`}
        conditions={lesson.clue_set.clues.map((clue, index) => ({ id: clue.id, label: ["HEAVY", "SPEED", "BLAST"][index], name: powerMeta[index].name, text: clue.text, gloss: ja(clue.text), color: powerMeta[index].color }))} />}
      <header className="game-topbar">
        <button className="round-button back-button pressable" onClick={onBack} aria-label="学習コースへ戻る"><Icon name="back" size={27} /></button>
        <div className="game-brand"><Icon name="book" size={24} /><span><strong>Word Physics</strong><small>WORDS INTO FORCES</small></span></div>
        <StatusPill shots={shots} goals={goals} blocks={blocks} />
        <div className="top-spacer" />
        {!freeMode && currentUnit ? <div className="lesson-picker"><b>LESSON {String(currentUnit.number).padStart(2, "0")}</b>{currentUnit.lessons.map((item, index) => {
          const globalIndex = allLessons.findIndex((candidate) => candidate.id === item.id);
          const unlocked = globalIndex === 0 || completed.has(allLessons[globalIndex - 1].id);
          const selected = item.id === lesson.id;
          return <button key={item.id} disabled={!unlocked || selected} className={selected ? "selected" : ""} onClick={() => switchLesson(item)}>{!unlocked && <Icon name="lock" size={13} />}{selected ? `STAGE ${item.code}` : index + 1}</button>;
        })}</div> : <div className="free-badge"><Icon name={mode === "daily" ? "calendar" : mode === "custom" ? "book" : "shuffle"} size={18} />{mode === "daily" ? `今日の一問・${formatDay(localDateKey())}` : mode === "custom" ? `カスタム・${lesson.code}` : "フリーチャレンジ"}</div>}
        <button className="round-button pressable" onClick={onSettings} aria-label="設定"><Icon name="gear" /></button>
      </header>

      <section className="clue-strip">
        {lesson.clue_set.clues.map((clue, index) => <div className="clue-card" style={{ "--power": powerMeta[index].color } as React.CSSProperties} key={clue.id}><span><b><span>{["HEAVY", "SPEED", "BLAST"][index]}</span> {powerMeta[index].name}</b><strong>{clue.text}</strong><small>{ja(clue.text)}</small></span></div>)}
      </section>

      <section ref={gameContentRef} className="game-content">
        <div ref={playfieldRef} className="playfield-card">
          <PhysicsCanvas
            layout={layout} profile={profile} armed={phase === "armed"} fireNonce={fireNonce} resetNonce={resetNonce}
            reduceMotion={window.matchMedia("(prefers-reduced-motion: reduce)").matches}
            onShotStart={() => { setShots((value) => value - 1); setPhase("resolving"); setMessage(profile?.canDetonate ? "Space かタップで爆発！" : "弾が飛んでいます…"); setCanDetonateNow(!!profile?.canDetonate); }}
            onDetonate={() => { setCanDetonateNow(false); setMessage("弾が飛んでいます…"); }}
            onState={(nextBlocks, nextGoals, nextMessage) => { setBlocks(nextBlocks); setGoals(nextGoals); if (nextMessage) setMessage(nextMessage); }}
            onShotEnd={(cleared) => {
              setCanDetonateNow(false);
              if (cleared) {
                setPhase("feedback"); setMessage("すべてのコアを壊しました！");
                // A cleared stage starts empty next time instead of restoring the winning word.
                draftGeneration.current += 1;
                void deleteLessonDraft(lesson.id);
                const improved = onCleared(lesson, shotsRef.current);
                setClearResult({ word: evaluation?.word ?? text.trim(), shotsLeft: shotsRef.current, improved });
                if (!freeMode) { const next = new Set(completed); next.add(lesson.id); setCompleted(next); }
              } else if (shotsRef.current > 0) { setPhase("armed"); setMessage(`あと${shotsRef.current}発。もう一度狙おう。`); }
              else { setPhase("feedback"); setMessage("弾を使い切りました。別の単語でもう一度試そう。"); }
            }}
          />
          {canDetonateNow && <DetonateHint variant="edge" />}
          <button type="button" className="mobile-panel-switch show-controls pressable" onClick={() => showPanel(controlsRef.current)}>単語を入力<Icon name="arrow-right" size={16} /></button>
          <button className="reset-board pressable" onClick={mode === "free" ? newChallenge : retry} aria-label={mode === "free" ? "新しいチャレンジ" : "盤面をリセット"}><Icon name={mode === "free" ? "shuffle" : "reset"} size={19} />{mode === "free" && <span>新しい盤面</span>}</button>
          {(phase === "armed" || phase === "resolving") && <div className="play-message" role="status" aria-live="polite"><Icon name={phase === "armed" ? "play" : "speed"} size={17} />{message}</div>}
          {(isCleared || isFailed) && <ResultCard
            cleared={isCleared} mode={mode} lesson={lesson} unitNumber={currentUnit?.number}
            nextStage={nextStage} nextLessonStage={nextLessonStage} result={clearResult} best={best[lesson.id]}
            onBack={onBack} onRetry={retry} onNewChallenge={newChallenge} onLoad={loadSession} onRestartCourse={restartCourse}
          />}
        </div>

        <aside ref={controlsRef} className={`control-rail ${isCleared || isFailed ? "is-locked" : ""}`}>
          <div className="rail-scroll">
            <button type="button" className="mobile-panel-switch show-stage pressable" onClick={() => showPanel(playfieldRef.current)}><Icon name="arrow-left" size={16} />盤面へ</button>
            <div className="spell-heading"><b>英単語を入力</b><span><Icon name="keyboard" size={17} />タップ / キーボード</span></div>
            <div className={`word-display ${text ? "has-word" : ""}`}>
              <input aria-label="英単語" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={text} maxLength={40} placeholder="Type a word…" readOnly={isCleared || isFailed} onChange={(event) => setWord(event.target.value)} />
              <button type="button" aria-label="単語を消す" onClick={() => setWord("")}>×</button>
            </div>
            <div className={`validation-line ${offlineMessage ? "error" : phase === "armed" ? "valid" : ""}`} role="status" aria-live="polite">
              <span>{offlineMessage || (phase === "checking" ? "英単語か確認しています…" : phase === "validating" ? "意味を採点しています…" : phase === "armed" ? "● 発射できます" : text && !validateWord(text) ? "英字・スペース・アポストロフィ・ハイフンを使ってください" : "1語がおすすめ・最大40文字")}</span>
              {offlineMessage && !online && apiAvailable && <button onClick={onEnableOnline}>オンライン採点を使う</button>}
            </div>
            <Keyboard onKey={(key) => key === "BACKSPACE" ? setWord(text.slice(0, -1)) : setWord(text + key)} />
            <hr />
            {evaluation ? <div className="evaluation" aria-live="polite">
              <div className="evaluation-title"><span><small>WORD / READY TO LAUNCH</small><strong>{evaluation.word}</strong></span>{evaluation.detected_parts_of_speech?.length ? <b><Icon name="tag" size={15} />{evaluation.detected_parts_of_speech.map((part) => ja(part.charAt(0).toUpperCase() + part.slice(1))).join("・")}</b> : null}</div>
              <div className="score-cards">{evaluation.tiers.map((tier, index) => <div key={powerMeta[index].name} style={{ "--power": powerMeta[index].color } as React.CSSProperties}>
                <small>{["HEAVY", "SPEED", "BLAST"][index]}</small><div className="score-meter" role="meter" aria-label={powerMeta[index].name} aria-valuemin={1} aria-valuemax={5} aria-valuenow={tier + 1}>{Array.from({ length: 5 }, (_, cell) => <span className={cell <= tier ? "filled" : ""} key={cell} />)}</div><strong>{tier + 1}<span> / 5</span></strong>
              </div>)}</div>
              {evaluation.reasons.length > 0 && <>
              <div className="reasons-head">
                <span className="eyebrow">WHY</span>
                <div className="segmented small" role="radiogroup" aria-label="解説の言語">
                  {([["en", "EN"], ["ja", "日本語"]] as const).map(([value, label]) => (
                    <button key={value} type="button" role="radio" aria-checked={reasonLanguage === value} className={reasonLanguage === value ? "selected" : ""} onClick={() => setReasonLanguage(value)}>{label}</button>
                  ))}
                </div>
              </div>
              {reasonLanguage === "ja" && (evaluation.reason_language ?? "en") === "en" && <p className="reasons-note">{online ? "日本語の解説を取得しています…" : "収録語の日本語解説には、オンライン採点の許可が必要です。"}</p>}
              <ul lang={evaluation.reason_language ?? "en"}>{evaluation.reasons.map((reason, index) => <li key={`${index}-${reason}`} style={{ "--power": powerMeta[index].color } as React.CSSProperties}><span className="reason-marker" aria-hidden="true" />{reason}</li>)}</ul>
              </>}
            </div> : null}
          </div>
          <div className="launch-dock"><button className={phase === "armed" ? "armed pressable" : ""} disabled={phase !== "armed"} onClick={launchFromDock}><Icon name={phase === "armed" ? "play" : "speed"} /><span>{phase === "armed" ? "引いて発射・またはタップ" : phase === "resolving" ? "ガラスを突破中…" : "英単語を作るとスリングを使えます"}</span>{phase === "armed" && <Icon name="arrow-left" />}</button></div>
        </aside>
      </section>
    </main>
  );
}

export default function App() {
  const [screen, setScreen] = useState<Screen>("course");
  const [selectedLesson, setSelectedLesson] = useState(allLessons[0]);
  const [mode, setMode] = useState<Mode>("course");
  const [best, setBestState] = useState(readBestRecords);
  const [daily, setDailyState] = useState(readDailyRecords);
  const [completed, setCompletedState] = useState(readCompleted);
  const [settings, setSettings] = useState(false);
  const [online, setOnlineState] = useState(() => localStorage.getItem("wordphysics.online") === "true");
  const [showOnlineGuide, setShowOnlineGuide] = useState(() => localStorage.getItem("wordphysics.online") === null);
  const [apiConnected, setApiConnected] = useState(false);
  const [theme, setThemeState] = useState<ThemeId>(() => document.documentElement.dataset.theme as ThemeId);
  const [reasonLanguage, setReasonLanguageState] = useState<ReasonLanguage>(() => localStorage.getItem("wordphysics.reasonLanguage") === "ja" ? "ja" : "en");

  useEffect(() => {
    const endpoint = (import.meta.env.VITE_SEMANTIC_API_URL as string | undefined)?.replace(/\/$/, "") ?? "";
    if (!endpoint && !import.meta.env.PROD) return;
    const controller = new AbortController();
    fetch(`${endpoint}/health`, { signal: controller.signal })
      .then((response) => response.ok ? response.json() : Promise.reject())
      .then((value: { ok?: boolean }) => setApiConnected(value.ok === true))
      .catch(() => setApiConnected(false));
    return () => controller.abort();
  }, []);

  const setCompleted = (value: Set<string>) => { setCompletedState(value); localStorage.setItem("wordphysics.completed", JSON.stringify([...value])); };
  const setOnline = (value: boolean) => { setOnlineState(value); localStorage.setItem("wordphysics.online", String(value)); };
  const setReasonLanguage = (value: ReasonLanguage) => { setReasonLanguageState(value); localStorage.setItem("wordphysics.reasonLanguage", value); };
  const finishOnlineGuide = (value: boolean) => { setOnline(value); setShowOnlineGuide(false); };
  const open = (lesson: Lesson, nextMode: Mode) => { setSelectedLesson(lesson); setMode(nextMode); setScreen("game"); window.scrollTo(0, 0); };
  const recordClear = (lesson: Lesson, shotsLeft: number) => {
    const result = recordBest(best, lesson.id, shotsLeft);
    if (result.records !== best) { setBestState(result.records); writeBestRecords(result.records); }
    if (lesson.id.startsWith("daily-")) {
      const day = lesson.id.slice("daily-".length);
      if (daily[day] === undefined || daily[day] < shotsLeft) {
        const next = { ...daily, [day]: shotsLeft };
        setDailyState(next); writeDailyRecords(next);
      }
    }
    return result.improved;
  };

  return <>
    {screen === "course"
      ? <CourseScreen completed={completed} best={best} daily={daily} onOpenLesson={(lesson) => open(lesson, "course")} onOpenCustom={(lesson) => open(lesson, "custom")} onFree={() => open(makeFreeChallenge().lesson, "free")} onDaily={() => open(makeDailyChallenge().lesson, "daily")} onSettings={() => setSettings(true)} />
      : <GameScreen key={`${mode}-${selectedLesson.id}`} initialLesson={selectedLesson} mode={mode} completed={completed} setCompleted={setCompleted} best={best} onCleared={recordClear} onBack={() => setScreen("course")} onSettings={() => setSettings(true)} online={online} onEnableOnline={() => setOnline(true)} reasonLanguage={reasonLanguage} setReasonLanguage={setReasonLanguage} />}
    {settings && <SettingsModal online={online} setOnline={setOnline} reasonLanguage={reasonLanguage} setReasonLanguage={setReasonLanguage} theme={theme} setTheme={(value) => { setThemeChoice(value); setThemeState(value); }} apiConnected={apiConnected} onClose={() => setSettings(false)} />}
    {showOnlineGuide && <OnlineScoringGuide apiConnected={apiConnected} onAllow={() => finishOnlineGuide(true)} onDecline={() => finishOnlineGuide(false)} />}
  </>;
}
